import { BaseTariffPage } from "@/components/tarif/BaseTariffPage";
import { Shield, CheckCircle2, Clock, Phone, FileText, Stethoscope, Users, Building2, Lock, Globe, Zap, HeartPulse, Briefcase, Database } from "lucide-react";

const ZURICH_COLOR = "#0052A5";

const ZurichTarif = () => {
  const heroFeatures = [
    { icon: Phone, title: "24/7 Cyber-Notfall", subtitle: "Sofortige Hilfe" },
    { icon: Shield, title: "Komplettschutz", subtitle: "Alle Cyber-Risiken" },
    { icon: Globe, title: "IT-Prävention", subtitle: "Cyber-Training" }
  ];

  const coverageAreas = [
    {
      icon: Zap,
      title: "Cyber-Sofortmaßnahmen",
      items: [
        "24/7 Cyber-Notfall-Hotline",
        "IT-Forensik und Angriffsanalyse",
        "Koordination IT-Sicherheitsexperten",
        "Krisenmanagement und Kommunikation",
        "Systemwiederherstellung und Recovery"
      ]
    },
    {
      icon: Clock,
      title: "Betriebsunterbrechung",
      items: [
        "Entgangener Gewinn durch Systemausfall",
        "Fortlaufende Betriebskosten",
        "Mehrkosten für Ersatzsysteme",
        "Kosten für temporäre Lösungen",
        "Ertragsausfalldeckung ohne Wartezeit"
      ]
    },
    {
      icon: Database,
      title: "Daten & IT-Systeme",
      items: [
        "Professionelle Datenwiederherstellung",
        "IT-forensische Analysen",
        "Systemrekonstruktion",
        "Backup-Restaurierung",
        "Wiederaufbau IT-Infrastruktur"
      ]
    },
    {
      icon: Lock,
      title: "Cyber-Haftpflicht",
      items: [
        "DSGVO-Bußgelder und Strafen",
        "Schadensersatzansprüche Dritter",
        "Rechtsanwalts- und Verfahrenskosten",
        "Cyber-Erpressung und Lösegelder",
        "Benachrichtigungskosten bei Datenpannen"
      ]
    }
  ];

  const targetGroups = [
    {
      icon: Stethoscope,
      title: "Arztpraxen aller Fachbereiche",
      description: "Hausärzte, Fachärzte, Gemeinschaftspraxen und Einzelpraxen mit elektronischer Patientenverwaltung",
      benefits: [
        "24/7-Cyber-Notfall-Hotline",
        "Umfassender Datenschutz",
        "DSGVO-konforme Absicherung",
        "Betriebsunterbrechung inklusive"
      ],
      recommendation: "Empfohlene Deckung: 100.000 - 300.000 €"
    },
    {
      icon: HeartPulse,
      title: "MVZ & Gesundheitszentren",
      description: "Medizinische Versorgungszentren mit vernetzter IT-Infrastruktur und mehreren Standorten",
      benefits: [
        "Multi-Standort-Absicherung",
        "Schutz komplexer IT-Netzwerke",
        "Umfassende Forensik",
        "Individuelle Deckungskonzepte"
      ],
      recommendation: "Empfohlene Deckung: 300.000 - 1.000.000 €"
    },
    {
      icon: Building2,
      title: "Kliniken & Krankenhäuser",
      description: "Große medizinische Einrichtungen mit kritischer digitaler Infrastruktur",
      benefits: [
        "Schutz medizintechnischer Geräte",
        "Notfallmanagement kritischer Systeme",
        "Höchste Deckungssummen",
        "Maßgeschneiderte Lösungen"
      ],
      recommendation: "Empfohlene Deckung: 1.000.000 - 5.000.000 €"
    },
    {
      icon: Briefcase,
      title: "Therapeutische Praxen",
      description: "Physiotherapie, Ergotherapie, Logopädie und psychotherapeutische Einrichtungen",
      benefits: [
        "Günstige Tarife für kleine Praxen",
        "Schutz digitaler Therapiepläne",
        "Mitarbeiterschulungen",
        "Schnelle Schadenbearbeitung"
      ],
      recommendation: "Empfohlene Deckung: 50.000 - 150.000 €"
    }
  ];

  const insuranceConditions = [
    {
      icon: Shield,
      title: "Versicherungssummen",
      description: "Flexibel von 50.000 EUR bis 5 Mio. EUR wählbar - optimal anpassbar an Ihre Praxisgröße"
    },
    {
      icon: FileText,
      title: "Selbstbeteiligung",
      description: "Wahlweise 0 EUR, 500 EUR oder 1.000 EUR SB - für optimale Beitragsgestaltung"
    },
    {
      icon: CheckCircle2,
      title: "Sofortschutz",
      description: "Keine Wartezeiten - voller Versicherungsschutz ab dem ersten Tag"
    },
    {
      icon: Clock,
      title: "Vertragslaufzeit",
      description: "1 Jahr mit automatischer Verlängerung, 3 Monate Kündigungsfrist zum Vertragsende"
    },
    {
      icon: Globe,
      title: "Präventionsservice",
      description: "Cyber-Awareness-Trainings, Phishing-Simulationen und IT-Sicherheitsberatung inklusive"
    },
    {
      icon: Users,
      title: "Vollständiger Schutz",
      description: "Gilt für alle Mitarbeiter, Geräte und beauftragte Dienstleister"
    }
  ];

  const faqs = [
    {
      question: "Was leistet die Zurich Cyberversicherung?",
      answer: "Die Zurich Cyberversicherung bietet umfassenden Schutz bei Cyber-Vorfällen: 24/7-Notfallhilfe, IT-Forensik, Datenwiederherstellung, Betriebsunterbrechungsschutz, Cyber-Erpressungsschutz, DSGVO-Bußgelder und Cyber-Haftpflicht. Zusätzlich sind präventive Schulungen und IT-Sicherheitsberatung enthalten."
    },
    {
      question: "Für wen ist die Zurich Cyberversicherung geeignet?",
      answer: "Die Versicherung eignet sich für alle medizinischen Einrichtungen: Arztpraxen, MVZ, Kliniken, Therapiepraxen und Heilberufe, die digitale Patientendaten verarbeiten. Durch flexible Deckungssummen von kleinen Praxen bis großen Kliniken skalierbar."
    },
    {
      question: "Wie hoch sollte die Versicherungssumme sein?",
      answer: "Die optimale Versicherungssumme richtet sich nach Praxisgröße und Risiko: Kleine Praxen 50.000-150.000 EUR, mittlere Praxen 150.000-300.000 EUR, MVZ 300.000-1.000.000 EUR, Kliniken 1-5 Mio. EUR."
    },
    {
      question: "Was kostet die Zurich Cyberversicherung?",
      answer: "Der Beitrag hängt von verschiedenen Faktoren ab: Fachrichtung, Praxisgröße, Jahresumsatz, IT-Sicherheitsmaßnahmen und gewählte Versicherungssumme. Für ein individuelles Angebot kontaktieren Sie uns für eine kostenlose Beratung."
    },
    {
      question: "Gibt es Wartezeiten?",
      answer: "Nein, es gibt keine Wartezeiten. Der vollständige Versicherungsschutz und die 24/7-Cyber-Hotline sind ab dem ersten Tag verfügbar."
    },
    {
      question: "Sind DSGVO-Bußgelder versichert?",
      answer: "Ja, die Zurich Cyberversicherung deckt DSGVO-Bußgelder ab, soweit rechtlich versicherbar. Ebenso enthalten sind Kosten für Datenschutzbeauftragte, Behördenmeldungen und Betroffenenbenachrichtigungen."
    },
    {
      question: "Wie funktioniert die 24/7-Cyber-Hotline?",
      answer: "Bei einem Cyber-Vorfall steht die Hotline rund um die Uhr zur Verfügung. Sie erhalten sofortigen Zugang zu IT-Spezialisten, die den Angriff stoppen, Schäden analysieren und die Wiederherstellung koordinieren."
    },
    {
      question: "Ist Cyber-Erpressung versichert?",
      answer: "Ja, die Versicherung deckt Cyber-Erpressungen ab: Lösegeldzahlungen (soweit rechtlich zulässig), Verhandlungskosten und Kosten für spezialisierte Cyber-Sicherheitsexperten."
    },
    {
      question: "Welche Präventionsleistungen sind enthalten?",
      answer: "Zurich bietet Cyber-Awareness-Schulungen für Mitarbeiter, regelmäßige Phishing-Simulationen, IT-Sicherheitsberatung und Online-Trainings zur Sensibilisierung für Cyber-Risiken."
    },
    {
      question: "Was passiert bei einem Datenleck?",
      answer: "Die Versicherung übernimmt alle Kosten: Benachrichtigung betroffener Personen, Meldung an Datenschutzbehörden, PR-Krisenmanagement, Rechtsberatung, Schadensersatzforderungen und eventuelle DSGVO-Bußgelder."
    }
  ];

  const documents = [
    {
      title: "Produktinformationsblatt Cyberversicherung",
      type: "PDF",
      url: "https://www.zurich.de/de-de/downloads/cyber-pib.pdf"
    },
    {
      title: "Versicherungsbedingungen (AVB)",
      type: "PDF",
      url: "https://www.zurich.de/de-de/downloads/cyber-avb.pdf"
    }
  ];

  return (
    <BaseTariffPage
      companyName="Zurich"
      companyColor={ZURICH_COLOR}
      productRating={{ value: 4.8, count: 398 }}
      priceRange="65-240"
      pageTitle="Zurich Cyberversicherung für Arztpraxen - Kompletter Cyber-Schutz"
      metaDescription="Die Zurich Cyberversicherung bietet Arztpraxen, MVZ und Kliniken umfassenden Schutz vor Cyber-Risiken mit 24/7-Notfallhilfe, DSGVO-Schutz und IT-Präventionsleistungen."
      metaKeywords="Zurich Cyberversicherung, Cyber-Versicherung Arztpraxis, Zurich Cyber-Schutz, Arztpraxis Cybersicherheit"
      canonicalUrl="https://praxis-versichert.de/tarifwissen/zurich"
      heroLabel="Internationaler Versicherungsexperte mit Cyber-Kompetenz"
      heroTitle={<>Zurich Cyberversicherung<br />für medizinische Einrichtungen</>}
      heroDescription="Umfassender Cyber-Schutz für Arztpraxen, MVZ und Kliniken mit 24/7-Notfallhilfe, vollständiger Betriebsunterbrechungsdeckung und präventiven IT-Sicherheitsleistungen. Von einem der weltweit führenden Versicherer."
      heroFeatures={heroFeatures}
      alertTitle="Warum Zurich Cyberversicherung?"
      alertDescription="Cyber-Angriffe auf Gesundheitseinrichtungen nehmen dramatisch zu - 2023 war jede dritte Arztpraxis betroffen. Die Zurich Cyberversicherung schützt Sie mit sofortiger 24/7-Hilfe, umfassenden Leistungen und über 150 Jahren Versicherungserfahrung."
      coverageAreas={coverageAreas}
      targetGroups={targetGroups}
      insuranceConditions={insuranceConditions}
      faqs={faqs}
      documents={documents}
    />
  );
};

export default ZurichTarif;
